import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaEye, FaEdit, FaTrash, FaPlus, FaSearch, FaClipboardList } from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";

import AssetView from "./AssetView";

const AssetManagementListChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Error Logging Service
  const [{ errorLogApi, user }] = useAtom(gbState);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleDelete = (row) => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this Asset?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(row.id),
        },
        {
          label: "No",
        },
      ],
    });
  };

  const deleteRecord = async (id) => {
    const userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };
    
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/asset/delete/${id}`, config);
      toast.success("Asset Deleted Successfully.");
      getDataFromServer();
    } catch (err) {
      errorLogApi(err, "AssetListChild", "/asset/delete");
      toast.error("Failed to delete asset.");
    }
  };

  const getDataFromServer = () => {
    const userToken = localStorage.getItem("token");
    const urlLink = `${process.env.REACT_APP_API_URL}/asset/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    
    axios(config)
      .then(function (response) {
        const oldData = response.data.assets;
        const updatedData = oldData.map((data) => {
          return {
            ...data,
            id: data?._id,
            asset_name: data?.asset_name,
            item_code: data?.item_code,
            description: data?.description,
            status: data?.status,
            church: data?.church_id,
          };
        });

        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
        toast.error("Failed to fetch assets.");
        errorLogApi(err, "AssetListChild", "/asset/get");
      });
  };

  useEffect(() => {
    getDataFromServer();
  }, [user?.church_id]);

  const filteredData = useMemo(() => {
    return (
      data?.filter((asset) =>
        Object.values(asset).some((value) =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      ) || []
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Asset Management</h2>
        <div className="flex space-x-2">
          <Button
            variant="contained"
            color="info"
            startIcon={<FaClipboardList />}
            onClick={() => navigate("/assets/inventory")}
          >
            Inventory
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FaPlus />}
            onClick={() => navigate("/assets/add")}
          >
            Add Asset
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search assets..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Item Code</TableCell>
              <TableCell>Asset Name</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          toggleModal();
                          setCurrentRecord(row);
                        }}
                      >
                        <FaEye className="text-blue-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate("/assets/update", { state: row })
                        }
                      >
                        <FaEdit className="text-gray-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row)}
                      >
                        <FaTrash className="text-red-600" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell>{row.item_code}</TableCell>
                <TableCell>{row.asset_name}</TableCell>
                <TableCell>
                  {row.status ? (
                    <span className="inline-block px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 text-xs font-semibold text-red-800 bg-red-200 rounded-full">
                      Inactive
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex items-center justify-between mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/assets/" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <h2 className="mb-4 text-xl font-bold">Asset Detail</h2>
            <AssetView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default AssetManagementListChild;
